import i18n from "i18next";
import {
	CustomerOrderDataGridApi,
	CustomerOrderDataGridApi_CustomerOrderDto,
} from "src/api/generated/erp/sales/customerOrder/api/customerOrderDataGridApi.ts";
import { getCustomerOrderStateLabels } from "src/api/generated/erp/db/types/enums/customerOrderState.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { dateColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { CustomerOrdersPage } from "src/components/views/erp/sales/customerOrder/CustomerOrdersPage.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface OrdersOfCustomerDataGridProps {
	customerId: number;
}

export const OrdersOfCustomerDataGrid = ({ customerId }: OrdersOfCustomerDataGridProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<ServerSideDataGridModel
			gridId={"DF64B19E320A43E9"}
			fetchData={(params) => CustomerOrderDataGridApi.searchCustomerOrders(params)}
			getDataModelResult={(response) => response}
			initialParams={{
				customerIdFilter: customerId,
				onlyDefaultSiteOrders: false,
				showInvoicedOrders: true,
				showCancelledOrders: true,
			}}
			getRowId={(row) => row.customerOrderId}
			render={({ dataGridProps }) => (
				<AavoDataGrid<CustomerOrderDataGridApi_CustomerOrderDto>
					disableMultipleRowSelection
					columns={[
						integerColumn({
							field: "customerOrderId",
							headerName: i18n.t("number_shortened"),
						}),
						textColumn({
							field: "siteName",
							headerName: i18n.t("site_short"),
						}),
						textColumn({
							field: "orderReference",
							headerName: i18n.t("reference"),
							width: 180,
						}),
						textColumn({
							field: "customerPoNo",
							headerName: i18n.t("customer_po_no"),
						}),
						enumColumn({
							field: "customerOrderState",
							headerName: i18n.t("state"),
							enumLabels: getCustomerOrderStateLabels(),
						}),
						dateColumn({
							field: "plannedDeliveryDate",
							headerName: i18n.t("planned_delivery_date_short"),
						}),
						dateColumn({
							field: "contractDate",
							headerName: i18n.t("contract_date"),
						}),
						textColumn({
							field: "responsiblePersonName",
							headerName: i18n.t("responsible_person"),
							width: 180,
						}),
						textColumn({
							field: "salespersonName",
							headerName: i18n.t("salesperson"),
							width: 180,
						}),
					]}
					openRow={openCustomerOrder}
					listViewOnMobile
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.customerOrdersOfCustomer.grid"),
						data: {
							customerId,
						},
						selectedRowPathSegment: (row) => `${i18n.t("customer_order")} ${row.customerOrderId}`,
						rowFields: ["customerOrderId", "customerId", "customerOrderOfferId", "orderReference"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);

	function openCustomerOrder(row: CustomerOrderDataGridApi_CustomerOrderDto) {
		openDialog({
			title: i18n.t("customer_order"),
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.customerOrder.ordersOfCustomerDataGrid.dialog"),
				pathSegments: [`${i18n.t("customer_order")} ${row.customerOrderId}`],
				data: {
					customerId,
					customerOrderId: row.customerOrderId,
				},
			},
			content: <CustomerOrdersPage onlyCustomerOrderToShowId={row.customerOrderId} />,
		});
	}
};
